import React, { ReactNode, useCallback, useState } from "react";
import classNames from "classnames";

import SidebarAccountBlock from "./sidebar-account-block";
import SidebarMenuHeader from "./sidebar-menu-header";
import SidebarMenuSection from "./sidebar-menu-section";
import SidebarWorkspaceSwitcher from "./sidebar-workspace-switcher";
import { SidebarProvider, useSidebar } from "./sidebar-context";
import type { SidebarItemLabelSize } from "./sidebar-menu.types";
import { SIDEBAR_PX_COLLAPSED, SIDEBAR_PX_EXPANDED } from "./sidebar-menu.classes";
import { flattenItems } from "./sidebar-menu.utils";
import {
  SidebarAccount,
  SidebarMenuItem,
  SidebarMenuSection as SidebarMenuSectionType,
  SidebarWorkspace,
} from "./sidebar-menu.types";

export interface SidebarMenuProps {
  children?: ReactNode;
  isCollapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (isCollapsed: boolean) => void;
  itemLabelSize?: SidebarItemLabelSize;
  className?: string;
}

const SidebarMenuRoot = ({
  children,
  isCollapsed: controlledCollapsed,
  defaultCollapsed = false,
  onCollapsedChange,
  itemLabelSize = "default",
  className,
}: SidebarMenuProps) => {
  const [uncontrolledCollapsed, setUncontrolledCollapsed] = useState(defaultCollapsed);
  const isCollapsed = controlledCollapsed ?? uncontrolledCollapsed;

  const setCollapsed = (nextValue: boolean) => {
    if (controlledCollapsed === undefined) {
      setUncontrolledCollapsed(nextValue);
    }
    onCollapsedChange?.(nextValue);
  };

  return (
    <SidebarProvider
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setCollapsed(!isCollapsed)}
      itemLabelSize={itemLabelSize}
    >
      <aside
        className={classNames(
          "relative z-20 flex h-full flex-col overflow-visible border-r border-gray-200 bg-white transition-all duration-200",
          isCollapsed ? "w-16" : "w-64",
          className,
        )}>
        {children}
      </aside>
    </SidebarProvider>
  );
};

interface SidebarItemsProps {
  sections: SidebarMenuSectionType[];
  activeItemId?: string;
  onItemSelect?: (item: SidebarMenuItem) => void;
  className?: string;
}

const SidebarItems = ({
  sections,
  activeItemId,
  onItemSelect,
  className,
}: SidebarItemsProps) => {
  const { isCollapsed } = useSidebar();
  const [openedNestedMenuIds, setOpenedNestedMenuIds] = useState<Record<string, boolean>>({});
  const allItems = sections.flatMap((section) => flattenItems(section.items));
  const isAnyNestedMenuOpen = Object.values(openedNestedMenuIds).some((isOpen) => isOpen);
  const hasActiveItemIdMatch = !!activeItemId && allItems.some((item) => item.id === activeItemId);
  const handleNestedMenuOpenChange = useCallback((itemId: string, isOpen: boolean) => {
    setOpenedNestedMenuIds((prevState) => {
      if (prevState[itemId] === isOpen) {
        return prevState;
      }

      return {
        ...prevState,
        [itemId]: isOpen,
      };
    });
  }, []);

  return (
    <div
      className={classNames(
        "min-h-0 flex-1 overflow-y-auto overflow-x-visible custom-scroll py-2 transition-all duration-200",
        isCollapsed ? SIDEBAR_PX_COLLAPSED : SIDEBAR_PX_EXPANDED,
        className,
      )}>
      {sections.map((section) => (
        <SidebarMenuSection
          key={section.id}
          section={section}
          activeItemId={activeItemId}
          hasActiveItemIdMatch={hasActiveItemIdMatch}
          onItemSelect={onItemSelect}
          isAnyNestedMenuOpen={isAnyNestedMenuOpen}
          onNestedMenuOpenChange={handleNestedMenuOpenChange}
        />
      ))}
    </div>
  );
};

export const SidebarMenu = Object.assign(SidebarMenuRoot, {
  Header: SidebarMenuHeader,
  WorkspaceSwitcher: SidebarWorkspaceSwitcher,
  Items: SidebarItems,
  Account: SidebarAccountBlock,
});

export default SidebarMenu;
